import { all, takeEvery, select, call, put } from 'redux-saga/effects';
import { BiEventType } from '@wix/da-shared-react/pkg/biLogger';
import {
  BIEventData,
  BIEventPayload,
} from '@wix/da-shared-react/pkg/biLogger/types';
import { logPageView } from '@wix/da-shared-react/pkg/biLogger/redux/actions';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import { APP_LOADED } from '../actions/app';
import { getActiveSection } from '../selectors/sections';
import { FETCH_SECTION_SUCCESS } from '../actions/sections';
import { getBiExtraData } from '../selectors/biLogger';
import { biFetchMoreFolderItems, biClickProfileNavBar } from '../actions/bi';
import { selectors, withOffset } from '@wix/da-shared-react/pkg/Stream';
import { exitDuperbrowseDone } from '@wix/da-shared-react/pkg/Duperbrowse/redux/actionCreators';
import {
  getFullViewFolderIds,
  getCurrentlyViewedFolderId,
} from '../selectors/gallectionSection';
import {
  GallectionResourceType,
  GallectionType,
} from '@wix/da-shared-react/pkg/types/gallection';

export function* logSearchSubmit(searchQuery) {
  if (typeof document === 'undefined') {
    return; // clientside only, as this event is also fired on SSR when search query is set from URL's query string
  }
  const event: BIEventData = {
    evid: BiEventType.GALLECTION_SEARCH_SUBMIT,
    search_text: searchQuery,
  };
  yield call(logBiEvent, event);
}

export function* pageViewHandler() {
  if (typeof document === 'undefined') {
    return; // clientside only
  }
  const extraData = yield select(getBiExtraData);
  const component = yield select(getActiveSection);
  const event: BIEventPayload = {
    view: 'profile',
    component,
  };
  yield put(logPageView({ ...extraData, ...event }));
}

function* fetchSectionHandler(action) {
  if (typeof document === 'undefined') {
    return; // clientside only
  }
  const extraData = yield select(getBiExtraData);
  const component = action.section;
  const event: BIEventPayload = {
    view: 'profile',
    component,
  };
  yield put(logPageView({ ...extraData, ...event }));
}

function* infiniteScrollHandler(action) {
  const { streamId } = action.payload;
  const eventComponent = yield select(getActiveSection);
  const extraData = yield select(getBiExtraData);
  const biTotalItemsCount = yield select(selectors.getTotalItemsCount);
  const currentlyViewedFolderId = yield select(getCurrentlyViewedFolderId);
  const streamOffset = yield select(state =>
    withOffset.selectors.getNextOffset(state, streamId)
  );
  const streamParams = yield select(state =>
    selectors.getStreamParamsSafe(state, streamId)
  );
  const fullviewFolderIds = yield select(getFullViewFolderIds);

  if (streamOffset === 0 && fullviewFolderIds.length > 1) {
    const typeid =
      streamParams.gallectionType === GallectionType.COLLECTION
        ? GallectionResourceType.COLLECTION
        : GallectionResourceType.GALLERY;
    const nextFolderEvent: BIEventData = {
      evid: BiEventType.SCROLL_TO_LOAD_NEXT_FOLDER,
      component: eventComponent,
      view: 'profile',
      typeid,
      itemid: currentlyViewedFolderId,
      next_itemid: streamParams.folderId,
      next_typeid: typeid,
    };
    yield call(logBiEvent, nextFolderEvent);
  }

  const event: BIEventData = {
    evid: BiEventType.SCROLL_TO_LOAD_MORE,
    component: eventComponent,
    view: 'profile',
    itemid: extraData.itemid,
    typeid: extraData.typeid,
    offset: biTotalItemsCount,
  };

  yield call(logBiEvent, event);
}

function* biClickProfileNavBarHandler(action) {
  const { linkName } = action.payload;
  const eventComponent = yield select(getActiveSection);
  const extraData = yield select(getBiExtraData);
  yield call(logBiEvent, {
    evid: BiEventType.PROFILE_NAV_BAR,
    view: 'profile',
    component: eventComponent,
    link_name: linkName,
    itemid: extraData.itemid,
    typeid: extraData.typeid,
  });
}

export default function* biLogger() {
  yield all([
    takeEvery([APP_LOADED, exitDuperbrowseDone], pageViewHandler),
    takeEvery(FETCH_SECTION_SUCCESS, fetchSectionHandler),
    takeEvery(biFetchMoreFolderItems, infiniteScrollHandler),
    takeEvery(biClickProfileNavBar, biClickProfileNavBarHandler),
  ]);
}
